import Phaser from "phaser";
import { CHARACTERS } from "@spieleparadies/shared";

import type { MountContext } from "../../registry";
import {
  effectiveMusicVolume,
  effectiveSfxVolume,
  useAudioStore,
} from "../../../state/useAudioStore";
import { usePresenceStore } from "../../../state/usePresenceStore";
import { bulletTextureKey, characterSpriteKey } from "../assets";
import { touchInput } from "../input";

interface PlaySceneData {
  ctx: MountContext;
}

interface RemotePlayer {
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  color: number;
  /** Ziel-Position vom letzten Netzwerk-Update; im update() smooth interpoliert. */
  targetX: number;
  targetY: number;
}

export interface ProgressEntry {
  id: string;
  name: string;
  /** Hex-Farbe als String, wie sie vom Server kommt (z. B. "#ec4899"). */
  color: string;
  /** 0..1 – Anteil der zurückgelegten Strecke zwischen Spawn und Anna. */
  progress: number;
  isMe: boolean;
  alive: boolean;
}

interface PlatformSpec {
  x: number;
  y: number;
  width: number; // in 64er Steps
}

interface GroundSegment {
  startX: number;
  endX: number;
}

const WORLD_WIDTH = 5600;
const WORLD_HEIGHT = 720;
const GROUND_Y = WORLD_HEIGHT - 64;
const PLAYER_SPEED = 270;
const JUMP_VELOCITY = -640;
const BULLET_SPEED = 720;
const BULLET_LIFETIME = 1200;
const SHOOT_COOLDOWN = 320;
const FIREBALL_SPEED = 220;
/** Maximaler Abstand zwischen Monster und einem Spieler, ab dem das Monster
 *  überhaupt schießt – verhindert, dass Geschosse irgendwo am anderen Ende
 *  der Welt erzeugt werden (kosten unnötig Physik + Tweens). */
const FLYING_FIRE_RANGE = 900;
const STARTING_LIVES = 3;

/**
 * Im Hochkant-Modus auf Touch-Geräten reservieren wir den unteren
 * Bildschirmbereich für die Touch-Steuerung. Der Wert muss mit dem
 * CSS der `.touch-controls-overlay` harmonieren.
 */
const PORTRAIT_CONTROLS_HEIGHT = 130;
const LANDSCAPE_CONTROLS_HEIGHT = 110;

/**
 * Höhe, die wir am unteren Bildrand für die Touch-Steuerung reservieren,
 * damit Boden und Steuerbuttons sich nicht überlagern. Auf Desktop ohne
 * Touch: 0. Im Hochkant-Modus: voller Wert. Im Querformat auf Handys:
 * etwas weniger, weil dort nicht so viel vertikaler Platz übrig ist.
 */
function causeLabel(cause: string): string {
  switch (cause) {
    case "monster":
      return "× Monster";
    case "flying-monster":
      return "× Flugmonster";
    case "spike":
      return "× Spike";
    case "lava":
      return "× Lava";
    case "fireball":
      return "× Feuerball";
    case "bullet":
      return "× Kugel";
    default:
      return "×";
  }
}

function controlsReservedHeight(w: number, h: number): number {
  if (navigator.maxTouchPoints === 0) return 0;
  // Schmaler Hochkant-Screen (typisch Smartphone hochkant)
  if (w < h && w < 900) return PORTRAIT_CONTROLS_HEIGHT;
  // Querkant-Smartphone: Breite groß, Höhe klein – Buttons brauchen trotzdem Platz
  if (w >= h && h < 600) return LANDSCAPE_CONTROLS_HEIGHT;
  return 0;
}

/**
 * Haupt-Gameplay-Scene für "Anna in Danger".
 *
 * - eigener Spieler ist Arcade-physikalisch
 * - Boden-/Fliegende Gegner werden serverseitig per Seed aufgestellt und
 *   per stabiler ID identifiziert (damit Kills synchron sind)
 * - Spieler kann Projektile abfeuern; Treffer auf Gegner geben XP, Treffer
 *   auf Mitspieler kosten ein Team-Leben (Friendly Fire)
 * - Mitspieler werden anhand serverseitiger Events gerendert
 */
export class PlayScene extends Phaser.Scene {
  private ctx!: MountContext;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keySpace!: Phaser.Input.Keyboard.Key;
  private shootKeys: Phaser.Input.Keyboard.Key[] = [];

  private hero!: Phaser.Physics.Arcade.Sprite;
  private platforms!: Phaser.Physics.Arcade.StaticGroup;
  private monsters!: Phaser.Physics.Arcade.Group;
  private flyingMonsters!: Phaser.Physics.Arcade.Group;
  private spikes!: Phaser.Physics.Arcade.StaticGroup;
  private lavaZones: Phaser.Geom.Rectangle[] = [];
  private bullets!: Phaser.Physics.Arcade.Group;
  /** Kugeln anderer Spieler – rein visuell, keine Schadens-Kollision. */
  private remoteBullets!: Phaser.Physics.Arcade.Group;
  private fireballs!: Phaser.Physics.Arcade.Group;
  private anna!: Phaser.Physics.Arcade.Sprite;

  private remotePlayers = new Map<string, RemotePlayer>();
  private remoteGroup!: Phaser.Physics.Arcade.Group;
  private lastNetSend = 0;
  private alive = true;
  private rescued = false;
  private seenMonster = false;
  private gameOver = false;
  private lastBroadcastX = 0;
  private lastShootAt = 0;
  private heroFacingRight = true;
  private lastTouchShootPulse = 0;
  private spawnX = 100;
  private lastProgressEmit = 0;
  private lastPresenceWrite = 0;
  private myXp = 0;
  private trainingMode = false;
  private myBulletKey = "bullet";
  private myBulletStyle = "human";

  // simple cooldown for movement sounds
  private lastJumpAt = 0;

  private musicSound: Phaser.Sound.BaseSound | null = null;
  private audioUnsubscribe: (() => void) | null = null;

  constructor() {
    super("PlayScene");
  }

  init(data: PlaySceneData) {
    this.ctx = data.ctx;
  }

  create() {
    this.trainingMode = this.ctx.room.gameMode === "training";

    const cam = this.cameras.main;
    cam.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    // Hintergrund als TileSprite: wiederholt sich entlang der Welt-Breite,
    // bleibt höhenmäßig immer = WORLD_HEIGHT und scrollt mit leichtem
    // Parallax-Effekt.
    this.add
      .tileSprite(0, 0, WORLD_WIDTH, WORLD_HEIGHT, "background")
      .setOrigin(0, 0)
      .setScrollFactor(0.3)
      .setDepth(-10);

    // Gruppen früh anlegen – buildLevel & spawn* füllen sie
    this.platforms = this.physics.add.staticGroup();
    this.spikes = this.physics.add.staticGroup();
    this.monsters = this.physics.add.group({ allowGravity: true });
    this.flyingMonsters = this.physics.add.group({ allowGravity: false });
    this.bullets = this.physics.add.group({ allowGravity: false });
    this.remoteBullets = this.physics.add.group({ allowGravity: false });
    this.fireballs = this.physics.add.group({ allowGravity: false });

    // Welt aufbauen
    this.buildLevel();

    // Held
    const myId = playerId(this);
    const mePlayer = this.ctx.room.players.find((p) => p.id === myId);
    const meIdx = this.ctx.room.players.findIndex((p) => p.id === myId);
    this.spawnX = 80 + (meIdx >= 0 ? meIdx * 28 : 0);
    const myCharacter = mePlayer?.character ?? "default";
    this.myBulletKey = bulletTextureKey(myCharacter);
    // Style merken, damit das Projektil charakter-passend bewegt werden kann
    const meChar = CHARACTERS.find((c) => c.id === myCharacter);
    this.myBulletStyle = meChar?.style ?? "human";
    this.hero = this.physics.add.sprite(this.spawnX, GROUND_Y - 80, characterSpriteKey(myCharacter));
    this.hero.setCollideWorldBounds(true);
    this.hero.setBounce(0.05);
    this.hero.setDepth(10);
    this.physics.add.collider(this.hero, this.platforms);

    // Boden-Monster patrouillieren – kollidieren mit Plattformen, Spikes
    // und Bodenfallen (sie laufen nicht in ihre eigenen Hindernisse rein).
    this.physics.add.collider(this.monsters, this.platforms);
    this.physics.add.collider(this.monsters, this.spikes);
    this.physics.add.overlap(this.hero, this.monsters, (_h, m) => {
      this.handleHeroHit("monster");
      void m;
    }, undefined, this);

    // Fliegende Monster: nicht mit Plattformen kollidieren
    this.physics.add.overlap(
      this.hero,
      this.flyingMonsters,
      () => this.handleHeroHit("flying-monster"),
      undefined,
      this,
    );

    // Spikes & Bodenfallen
    this.physics.add.overlap(this.hero, this.spikes, () => this.handleHeroHit("spike"), undefined, this);

    // Anna
    this.anna = this.physics.add.sprite(WORLD_WIDTH - 120, GROUND_Y - 32, "anna");
    (this.anna.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.anna.setImmovable(true);
    this.physics.add.overlap(this.hero, this.anna, () => this.handleRescue(), undefined, this);

    // Projektil-Kollisionen
    this.physics.add.overlap(
      this.bullets,
      this.monsters,
      (bullet, monster) =>
        this.handleBulletHitMonster(
          bullet as Phaser.Physics.Arcade.Sprite,
          monster as Phaser.Physics.Arcade.Sprite,
        ),
      undefined,
      this,
    );
    this.physics.add.overlap(
      this.bullets,
      this.flyingMonsters,
      (bullet, monster) =>
        this.handleBulletHitMonster(
          bullet as Phaser.Physics.Arcade.Sprite,
          monster as Phaser.Physics.Arcade.Sprite,
        ),
      undefined,
      this,
    );
    this.physics.add.collider(
      this.bullets,
      this.platforms,
      (bullet) => {
        (bullet as Phaser.Physics.Arcade.Sprite).destroy();
      },
      undefined,
      this,
    );
    this.physics.add.overlap(
      this.hero,
      this.fireballs,
      (_h, fireball) => this.handleFireballHit(fireball as Phaser.Physics.Arcade.Sprite),
      undefined,
      this,
    );
    // Feuerbälle fliegen DURCH Plattformen (magisches Feuer) – nur der
    // Held kann getroffen werden. Ohne diesen Collider überleben die
    // Feuerbälle ihre volle Lebenszeit.

    // Kamera folgt dem Helden + Zoom an Display anpassen
    cam.startFollow(this.hero, true, 0.1, 0.1);
    this.applyViewportZoom();
    this.scale.on("resize", this.applyViewportZoom, this);

    // Input
    if (!this.input.keyboard) {
      throw new Error("Keine Tastatur-Eingabe verfügbar");
    }
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keyW = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keyA = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keySpace = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    // Mehrere Tasten zum Schießen, damit es egal ist welche Hand wo liegt:
    // F (klassisch bei WASD), J (Index der rechten Hand), K und Shift.
    this.shootKeys = [
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F),
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.J),
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.K),
      this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT),
    ];

    // Mitspieler: Physics-Gruppe für Kollision mit dem eigenen Helden.
    // Remote-Sprites sind immovable (werden per Netzwerk positioniert).
    this.remoteGroup = this.physics.add.group({
      immovable: true,
      allowGravity: false,
    });
    this.physics.add.collider(this.hero, this.remoteGroup);

    for (const player of this.ctx.room.players) {
      if (player.id !== myId) {
        this.ensureRemote(player.id, player.name, player.color, player.character);
      }
    }

    // Fliegende Monster feuern jetzt server-autoritär. Wir reagieren in
    // `handleGameEvent` auf `anna:monster-fire` und erzeugen den Feuerball
    // dann lokal – so sehen alle Clients dieselben Geschosse, auch wenn das
    // Monster bei einem außerhalb des Sichtfelds liegt.

    // Intro nur einmal pro Game-Instanz – auch wenn die Scene aus irgendeinem
    // Grund neu startet (Respawn löst dies eigentlich nicht aus, aber wir
    // wollen es defensiv absichern). Das Flag liegt auf dem Phaser-Game,
    // also wird es beim Verlassen/Neueinstieg ins Spiel sauber zurückgesetzt.
    if (!this.game.registry.get("rette-anna:intro-played")) {
      this.tryPlay("intro", { volume: 0.9 });
      this.game.registry.set("rette-anna:intro-played", true);
    }

    if (this.cache.audio.exists("music")) {
      const musicVol = effectiveMusicVolume(useAudioStore.getState());
      this.musicSound = this.sound.add("music", { loop: true, volume: musicVol });
      this.musicSound.play();
    }

    // Lautstärke live aktualisieren, wenn der Spieler an den Reglern dreht
    this.audioUnsubscribe = useAudioStore.subscribe((state) => {
      if (this.musicSound) {
        const vol = effectiveMusicVolume(state);
        const music = this.musicSound as Phaser.Sound.BaseSound & { volume?: number };
        if (typeof music.volume === "number") music.volume = vol;
      }
    });

    // Socket-Events
    this.bindSocketEvents();

    // Initial HUD-Event in den UI-Scene-EventBus
    this.scene.get("UIScene").events.emit("hud", {
      lives: STARTING_LIVES,
      maxLives: STARTING_LIVES,
      xp: 0,
      training: this.trainingMode,
    });

    this.events.once("shutdown", this.cleanupSocket, this);
  }

  override update(time: number, deltaMs: number) {
    if (this.gameOver || this.rescued) {
      this.hero.setVelocityX(0);
      return;
    }
    if (!this.alive) {
      this.hero.setVelocityX(0);
    } else {
      const left = this.cursors.left?.isDown || this.keyA.isDown || touchInput.left;
      const right = this.cursors.right?.isDown || this.keyD.isDown || touchInput.right;
      const jump =
        this.cursors.up?.isDown || this.keyW.isDown || this.keySpace.isDown || touchInput.jump;
      const shootHeld = this.shootKeys.some((k) => k.isDown) || touchInput.shoot;
      const shootPulse = touchInput.shootPulse !== this.lastTouchShootPulse;

      if (left) {
        this.hero.setVelocityX(-PLAYER_SPEED);
        this.hero.setFlipX(true);
        this.heroFacingRight = false;
      } else if (right) {
        this.hero.setVelocityX(PLAYER_SPEED);
        this.hero.setFlipX(false);
        this.heroFacingRight = true;
      } else {
        this.hero.setVelocityX(0);
      }

      const onGround = this.hero.body!.blocked.down || this.hero.body!.touching.down;
      if (jump && onGround && time - this.lastJumpAt > 220) {
        this.hero.setVelocityY(JUMP_VELOCITY);
        this.lastJumpAt = time;
        this.tryPlay("jump", { volume: 0.7 });
      }

      if ((shootHeld || shootPulse) && time - this.lastShootAt > SHOOT_COOLDOWN) {
        this.shootBullet(time);
      }
      this.lastTouchShootPulse = touchInput.shootPulse;
    }

    // Lava-Death-Check
    if (this.alive) {
      for (const lava of this.lavaZones) {
        if (lava.contains(this.hero.x, this.hero.y + 20)) {
          this.handleHeroHit("lava");
          break;
        }
      }
    }

    // "Monster gesehen"-Kommentar einmalig pro Level
    if (!this.seenMonster) {
      const wv = this.cameras.main.worldView;
      const enemies = [
        ...this.monsters.getChildren(),
        ...this.flyingMonsters.getChildren(),
      ];
      for (const monsterObj of enemies) {
        const m = monsterObj as Phaser.Physics.Arcade.Sprite;
        if (wv.contains(m.x, m.y)) {
          this.seenMonster = true;
          this.tryPlay("monster", { volume: 0.8 });
          break;
        }
      }
    }

    // Fliegende Monster: simple Sinuswelle plus leichtes Tracking
    this.flyingMonsters.getChildren().forEach((obj) => {
      const m = obj as Phaser.Physics.Arcade.Sprite;
      const baseY = m.getData("baseY") as number;
      m.y = baseY + Math.sin(time / 400 + (m.getData("phase") as number)) * 24;
    });

    // Mitspieler-Sprite-Positionen smooth Richtung Ziel ziehen. 0.25 als
    // Lerp-Faktor ergibt eine "weiche" Bewegung ohne dass sich Tweens
    // anhäufen (deutlich performanter als pro Update einen neuen Tween).
    for (const remote of this.remotePlayers.values()) {
      remote.sprite.x = Phaser.Math.Linear(remote.sprite.x, remote.targetX, 0.25);
      remote.sprite.y = Phaser.Math.Linear(remote.sprite.y, remote.targetY, 0.25);
      remote.label.x = remote.sprite.x;
      remote.label.y = remote.sprite.y - 38;
    }

    // Eigene Position in den Presence-Store schreiben (für Proximity-Voice).
    // Throttle auf ~10 Hz – häufiger zu schreiben verursacht nur GC-Druck,
    // ohne dass der Voice-Lautstärke-Algorithmus davon profitiert.
    const me = playerId(this);
    if (me && time - this.lastPresenceWrite > 100) {
      usePresenceStore.getState().setPosition(me, this.hero.x, this.hero.y);
      this.lastPresenceWrite = time;
    }

    // Position broadcasten (max alle ~50ms, nur wenn signifikant bewegt)
    if (this.alive && time - this.lastNetSend > 50) {
      const dx = Math.abs(this.hero.x - this.lastBroadcastX);
      if (dx > 1) {
        this.ctx.socket.emit("game:event", {
          type: "rette-anna:state",
          payload: {
            x: this.hero.x,
            y: this.hero.y,
            flip: this.hero.flipX,
            alive: this.alive,
          },
        });
        this.lastBroadcastX = this.hero.x;
        this.lastNetSend = time;
      }
    }

    // Eigene + Remote-Bullets aufräumen
    for (const group of [this.bullets, this.remoteBullets]) {
      group.getChildren().forEach((obj) => {
        const b = obj as Phaser.Physics.Arcade.Sprite;
        if (b.x < -100 || b.x > WORLD_WIDTH + 100) b.destroy();
      });
    }

    // Progress-HUD: alle ~200ms aktualisieren – reicht für einen flüssigen
    // Balken und kostet kaum CPU.
    if (time - this.lastProgressEmit > 200) {
      this.emitProgress();
      this.lastProgressEmit = time;
    }

    void deltaMs;
  }

  /**
   * Sammelt die aktuelle Strecke aller Spieler (eigener Held + Remotes)
   * und schickt sie an die UIScene, die daraus den Progress-Balken zeichnet.
   * 0 = Spawn, 1 = Anna. Werte unter 0 werden auf 0 geclampt; über 1 (an Anna
   * vorbei) auf 1.
   */
  private emitProgress() {
    const goal = this.anna ? this.anna.x : WORLD_WIDTH - 120;
    const span = Math.max(1, goal - this.spawnX);
    const me = playerId(this);
    const myPlayer = this.ctx.room.players.find((p) => p.id === me);
    const entries: ProgressEntry[] = [];
    if (myPlayer) {
      entries.push({
        id: myPlayer.id,
        name: myPlayer.name,
        color: myPlayer.color,
        progress: Phaser.Math.Clamp((this.hero.x - this.spawnX) / span, 0, 1),
        isMe: true,
        alive: this.alive,
      });
    }
    for (const [id, remote] of this.remotePlayers) {
      const p = this.ctx.room.players.find((rp) => rp.id === id);
      if (!p) continue;
      entries.push({
        id,
        name: p.name,
        color: p.color,
        progress: Phaser.Math.Clamp((remote.sprite.x - this.spawnX) / span, 0, 1),
        isMe: false,
        alive: true,
      });
    }
    this.scene.get("UIScene").events.emit("progress", entries);
  }

  // ---------- Level / Welt ----------

  private buildLevel() {
    // Bodensegmente mit Lücken. Lücken sind die Lava-Felder.
    const groundSegments: GroundSegment[] = [
      { startX: 0, endX: 720 },
      { startX: 920, endX: 1480 },
      { startX: 1740, endX: 2240 },
      { startX: 2540, endX: 3060 },
      { startX: 3340, endX: 3900 },
      { startX: 4140, endX: 4720 },
      { startX: 4960, endX: WORLD_WIDTH },
    ];

    for (const seg of groundSegments) {
      for (let x = seg.startX; x < seg.endX; x += 64) {
        this.platforms
          .create(x, GROUND_Y + 16, "platform")
          .setOrigin(0, 0.5)
          .refreshBody();
      }
    }

    // Lava zwischen den Bodensegmenten
    for (let i = 0; i < groundSegments.length - 1; i += 1) {
      const a = groundSegments[i];
      const b = groundSegments[i + 1];
      if (!a || !b) continue;
      const lavaX = a.endX;
      const lavaW = b.startX - a.endX;
      if (lavaW <= 0) continue;
      // Lava-Optik
      for (let x = lavaX; x < lavaX + lavaW; x += 64) {
        const w = Math.min(64, lavaX + lavaW - x);
        const lava = this.add.image(x, GROUND_Y + 16, "lava-tile").setOrigin(0, 0.5);
        if (w !== 64) lava.setCrop(0, 0, w, 32);
        this.tweens.add({
          targets: lava,
          y: GROUND_Y + 18,
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: "Sine.InOut",
          delay: x * 0.02,
        });
      }
      // Death-Hitbox (etwas oberhalb der Lava beginnend)
      this.lavaZones.push(
        new Phaser.Geom.Rectangle(lavaX, GROUND_Y - 8, lavaW, 80),
      );
    }

    // Plattformen über den Lava-Lücken (Pflichtsprünge)
    const platforms: PlatformSpec[] = [
      // über Lava 1 (720-920)
      { x: 780, y: GROUND_Y - 120, width: 1 },
      { x: 860, y: GROUND_Y - 180, width: 1 },
      // über Lava 2 (1480-1740)
      { x: 1520, y: GROUND_Y - 140, width: 1 },
      { x: 1620, y: GROUND_Y - 210, width: 1 },
      { x: 1700, y: GROUND_Y - 140, width: 1 },
      // über Lava 3 (2240-2540)
      { x: 2280, y: GROUND_Y - 160, width: 1 },
      { x: 2380, y: GROUND_Y - 240, width: 1 },
      { x: 2480, y: GROUND_Y - 160, width: 1 },
      // über Lava 4 (3060-3340)
      { x: 3100, y: GROUND_Y - 200, width: 1 },
      { x: 3220, y: GROUND_Y - 280, width: 1 },
      // über Lava 5 (3900-4140)
      { x: 3940, y: GROUND_Y - 150, width: 1 },
      { x: 4060, y: GROUND_Y - 220, width: 1 },
      // über Lava 6 (4720-4960)
      { x: 4760, y: GROUND_Y - 200, width: 1 },
      { x: 4860, y: GROUND_Y - 280, width: 1 },
      // dekorative Plattformen auf festem Boden
      { x: 320, y: GROUND_Y - 200, width: 2 },
      { x: 560, y: GROUND_Y - 260, width: 1 },
      { x: 1040, y: GROUND_Y - 220, width: 2 },
      { x: 1280, y: GROUND_Y - 320, width: 1 },
      { x: 1900, y: GROUND_Y - 240, width: 2 },
      { x: 2700, y: GROUND_Y - 220, width: 2 },
      { x: 3500, y: GROUND_Y - 240, width: 2 },
      { x: 4300, y: GROUND_Y - 260, width: 2 },
      { x: 5160, y: GROUND_Y - 200, width: 2 },
    ];
    for (const p of platforms) {
      for (let i = 0; i < p.width; i += 1) {
        this.platforms
          .create(p.x + i * 64, p.y, "platform")
          .setOrigin(0, 0.5)
          .refreshBody();
      }
    }

    // Spike-Cluster (mehrere hintereinander) – nur auf festem Boden!
    // Der erste Cluster ist bewusst etwas weiter rechts, damit man nach dem
    // Spawn ein paar Sekunden Zeit zum Üben hat.
    const spikeClusters: Array<[number, number]> = [
      [460, 3], // x, count
      [1100, 4],
      [1820, 3],
      [2680, 4],
      [3460, 5],
      [4220, 4],
      [5060, 3],
    ];
    for (const [startX, count] of spikeClusters) {
      for (let i = 0; i < count; i += 1) {
        const spike = this.spikes.create(startX + i * 22, GROUND_Y - 4, "spike") as Phaser.Physics.Arcade.Sprite;
        spike.setOrigin(0.5, 1);
        spike.setData("kind", "spike");
        spike.refreshBody();
      }
    }

    // Bodenfallen (statisch, breitere metallische Optik)
    const trapPositions = [640, 1340, 2080, 2900, 3680, 4480, 5240];
    for (const x of trapPositions) {
      const trap = this.spikes
        .create(x, GROUND_Y + 2, "ground-trap") as Phaser.Physics.Arcade.Sprite;
      trap.setOrigin(0.5, 1);
      trap.setData("kind", "ground-trap");
      trap.setData("phase", Math.random() * 2);
      trap.refreshBody();
    }

    // Boden-Monster (patrouillieren auf festen Segmenten)
    const groundEnemies: Array<{ id: string; x: number; range: number }> = [
      { id: "g1", x: 500, range: 100 },
      { id: "g2", x: 1140, range: 140 },
      { id: "g3", x: 1980, range: 100 },
      { id: "g4", x: 2820, range: 150 },
      { id: "g5", x: 3640, range: 120 },
      { id: "g6", x: 4380, range: 160 },
      { id: "g7", x: 5160, range: 140 },
    ];
    for (const e of groundEnemies) {
      const m = this.monsters.create(e.x, GROUND_Y - 60, "monster") as Phaser.Physics.Arcade.Sprite;
      m.setCollideWorldBounds(true);
      m.setVelocityX(Math.random() > 0.5 ? -70 : 70);
      m.setBounce(1, 0);
      m.setData("enemyId", e.id);
      m.setData("range", e.range);
      m.setData("origin", e.x);
    }
    this.time.addEvent({
      delay: 200,
      loop: true,
      callback: () => {
        this.monsters.getChildren().forEach((obj) => {
          const m = obj as Phaser.Physics.Arcade.Sprite;
          const body = m.body as Phaser.Physics.Arcade.Body | null;
          if (!body) return;
          const origin = m.getData("origin") as number;
          const range = m.getData("range") as number;
          // Beim Hindernis (Spike/Bodenfalle/Welt-Rand) sofort umdrehen
          if (body.blocked.left) {
            m.setVelocityX(70);
            m.setFlipX(false);
          } else if (body.blocked.right) {
            m.setVelocityX(-70);
            m.setFlipX(true);
          } else if (m.x < origin - range) {
            m.setVelocityX(70);
            m.setFlipX(false);
          } else if (m.x > origin + range) {
            m.setVelocityX(-70);
            m.setFlipX(true);
          }
        });
      },
    });

    // Fliegende Monster (schweben + schießen)
    const flyingEnemies: Array<{ id: string; x: number; y: number }> = [
      { id: "f1", x: 900, y: GROUND_Y - 220 },
      { id: "f2", x: 1640, y: GROUND_Y - 280 },
      { id: "f3", x: 2400, y: GROUND_Y - 320 },
      { id: "f4", x: 3220, y: GROUND_Y - 300 },
      { id: "f5", x: 4080, y: GROUND_Y - 280 },
      { id: "f6", x: 4840, y: GROUND_Y - 320 },
    ];
    for (const e of flyingEnemies) {
      const m = this.flyingMonsters.create(e.x, e.y, "flying-monster") as Phaser.Physics.Arcade.Sprite;
      m.setData("enemyId", e.id);
      m.setData("baseY", e.y);
      m.setData("phase", Math.random() * Math.PI * 2);
      // Kleines Patrouillen-Tween für leichtes Hin-und-Her
      this.tweens.add({
        targets: m,
        x: e.x + 80,
        duration: 3000,
        yoyo: true,
        repeat: -1,
        ease: "Sine.InOut",
      });
    }
  }

  // ---------- Schießen ----------

  private shootBullet(time: number) {
    this.lastShootAt = time;
    const dir = this.heroFacingRight ? 1 : -1;
    const x = this.hero.x + dir * 24;
    const y = this.hero.y - 4;
    const bullet = this.bullets.create(x, y, this.myBulletKey) as Phaser.Physics.Arcade.Sprite;
    bullet.setVelocityX(dir * BULLET_SPEED);
    bullet.setData("born", time);
    (bullet.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    bullet.setDepth(9);
    this.applyBulletStyle(bullet, this.myBulletStyle, dir);
    // Eigener Schuss-Sound (Raum-spezifisch überschreibbar). Falls weder
    // Custom-Recording noch Default existiert, gibt's einfach kein SFX –
    // `tryPlay` prüft das.
    this.tryPlay("shoot", { volume: 0.85 });

    this.ctx.socket.emit("game:event", {
      type: "rette-anna:shoot",
      payload: {
        x,
        y,
        dir,
        bulletKey: this.myBulletKey,
        bulletStyle: this.myBulletStyle,
      },
    });
  }

  /**
   * Erzeugt eine rein visuelle Kugel eines Mitspielers. Keine
   * Schadens-Kollision — der Schaden wird vom Schützen berechnet.
   */
  private spawnRemoteBullet(payload: Record<string, unknown> | undefined) {
    if (!payload) return;
    const x = payload.x as number;
    const y = payload.y as number;
    const dir = payload.dir as number;
    const bulletKey = (payload.bulletKey as string) || "bullet";
    const bulletStyle = (payload.bulletStyle as string) || "human";
    if (typeof x !== "number" || typeof y !== "number" || typeof dir !== "number") return;

    const textureKey = this.textures.exists(bulletKey) ? bulletKey : "bullet";
    const bullet = this.remoteBullets.create(x, y, textureKey) as Phaser.Physics.Arcade.Sprite;
    bullet.setVelocityX(dir * BULLET_SPEED);
    (bullet.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    bullet.setDepth(9);
    bullet.setAlpha(0.75);
    this.applyBulletStyle(bullet, bulletStyle, dir);

    this.time.delayedCall(BULLET_LIFETIME, () => {
      if (bullet.active) bullet.destroy();
    });
  }

  /**
   * Verleiht dem Projektil eine charakter-passende Bewegung – Schneeball rollt,
   * Mickey-Stern dreht sich, Genie funkelt, Buzz-Laser fliegt gerade.
   */
  private applyBulletStyle(
    bullet: Phaser.Physics.Arcade.Sprite,
    style: string,
    dir: number,
  ) {
    switch (style) {
      case "snowman":
      case "lion":
      case "mouse":
        bullet.setAngularVelocity(dir * 720);
        break;
      case "stitch":
      case "genie":
        this.tweens.add({
          targets: bullet,
          scale: { from: 0.85, to: 1.15 },
          duration: 160,
          yoyo: true,
          repeat: -1,
          ease: "Sine.InOut",
        });
        break;
      case "spaceman":
        // Laser: horizontal, leicht in Schuss-Richtung "gestreckt"
        bullet.setFlipX(dir < 0);
        bullet.setScale(1.05, 1);
        break;
      default:
        // Standard-Kugel: dezentes Rotieren
        bullet.setAngularVelocity(dir * 360);
    }
  }

  /**
   * Erzeugt einen Feuerball für ein bestimmtes fliegendes Monster. Die ID
   * stammt vom Server (`anna:monster-fire`-Event), damit alle Clients
   * synchron dieselben Geschosse sehen. Zielrichtung ist der jeweils
   * nächstgelegene Spieler aus lokaler Sicht.
   */
  private spawnFireballFromMonster(monsterId: string) {
    if (this.gameOver || this.rescued) return;
    let monster: Phaser.Physics.Arcade.Sprite | undefined;
    this.flyingMonsters.getChildren().some((obj) => {
      const m = obj as Phaser.Physics.Arcade.Sprite;
      if (m.getData("enemyId") === monsterId) {
        monster = m;
        return true;
      }
      return false;
    });
    if (!monster) return;

    // Ziel: nächster lebender Spieler (hero + remotes)
    const targets: Array<{ x: number; y: number }> = [];
    if (this.alive) targets.push({ x: this.hero.x, y: this.hero.y });
    for (const remote of this.remotePlayers.values()) {
      targets.push({ x: remote.sprite.x, y: remote.sprite.y });
    }
    if (targets.length === 0) return;

    let best = targets[0]!;
    let bestDist = Math.hypot(best.x - monster.x, best.y - monster.y);
    for (let i = 1; i < targets.length; i++) {
      const t = targets[i]!;
      const d = Math.hypot(t.x - monster.x, t.y - monster.y);
      if (d < bestDist) {
        best = t;
        bestDist = d;
      }
    }

    // Range-Check: weit entfernte Spieler löst das Monster nicht aus.
    // Spart Performance, weil sonst Geschosse spawned werden, die niemand
    // jemals sieht.
    if (bestDist > FLYING_FIRE_RANGE) return;

    const dx = best.x - monster.x;
    const dy = best.y - monster.y;
    const len = Math.hypot(dx, dy) || 1;
    const fireball = this.fireballs.create(monster.x, monster.y, "fireball") as Phaser.Physics.Arcade.Sprite;
    (fireball.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    fireball.setVelocity((dx / len) * FIREBALL_SPEED, (dy / len) * FIREBALL_SPEED);
    fireball.setDepth(8);
    this.time.delayedCall(3500, () => {
      if (fireball.active) fireball.destroy();
    });
  }

  private handleBulletHitMonster(
    bullet: Phaser.Physics.Arcade.Sprite,
    monster: Phaser.Physics.Arcade.Sprite,
  ) {
    const enemyId = monster.getData("enemyId") as string | undefined;
    if (!enemyId) return;
    bullet.destroy();
    // Lokal direkt entfernen, damit das Feedback sofort kommt – Server bestätigt anschließend
    monster.destroy();
    this.ctx.socket.emit("game:event", {
      type: "anna:enemy-killed",
      payload: { enemyId },
    });
    this.spawnHitEffect(monster.x, monster.y);
  }

  private handleFireballHit(fireball: Phaser.Physics.Arcade.Sprite) {
    if (!this.alive || this.gameOver) return;
    fireball.destroy();
    this.handleHeroHit("fireball");
  }

  private spawnHitEffect(x: number, y: number) {
    const circle = this.add.circle(x, y, 6, 0xfde047, 0.9).setDepth(12);
    this.tweens.add({
      targets: circle,
      scale: 3,
      alpha: 0,
      duration: 320,
      onComplete: () => circle.destroy(),
    });
  }

  // ---------- Spiel-Events ----------

  private handleHeroHit(cause: string) {
    if (!this.alive || this.rescued || this.gameOver) return;
    this.alive = false;
    this.tryPlay("die", { volume: 0.9 });
    this.hero.setTint(0xff5555);
    this.hero.setVelocity(0, -300);

    this.ctx.socket.emit("game:event", {
      type: "anna:player-died",
      payload: { x: this.hero.x, y: this.hero.y, cause },
    });
    // alter Event-Typ für Backward-Compat (Position-Relay)
    this.ctx.socket.emit("game:event", {
      type: "rette-anna:died",
      payload: { x: this.hero.x, y: this.hero.y },
    });

    this.time.delayedCall(1500, () => {
      if (!this.gameOver) this.respawn();
    });
  }

  private respawn() {
    this.hero.clearTint();
    this.hero.setPosition(this.spawnX, GROUND_Y - 80);
    this.hero.setVelocity(0, 0);
    this.alive = true;
  }

  /**
   * Spielt für einen anderen Spieler die Treffer-Animation ab, sobald der
   * Server `anna:lives` mit `deadPlayerId !== meinId` schickt. So sehen alle
   * Mitspieler, wenn jemand stirbt – nicht nur der Betroffene selbst.
   */
  private showRemotePlayerHit(remoteId: string, cause: string) {
    const remote = this.remotePlayers.get(remoteId);
    if (!remote) return;
    const sprite = remote.sprite as Phaser.Physics.Arcade.Sprite;
    sprite.setTint(0xff5555);
    this.spawnHitEffect(sprite.x, sprite.y);
    // Etwas leiser, damit eigene Tode lauter bleiben als die der anderen.
    this.tryPlay("die", { volume: 0.45 });
    // Kurzer Schlenker nach oben – nur falls es ein Physics-Body ist.
    const body = sprite.body as Phaser.Physics.Arcade.Body | undefined;
    if (body && typeof body.setVelocity === "function") {
      body.setVelocity(0, -180);
    }
    // Mini-Label, das die Ursache anzeigt – schnell ein-/ausblenden.
    if (cause && cause !== "unknown") {
      const tag = this.add
        .text(sprite.x, sprite.y - 60, causeLabel(cause), {
          fontFamily: "Inter, sans-serif",
          fontSize: "16px",
          color: "#fde047",
          stroke: "#7f1d1d",
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(13);
      this.tweens.add({
        targets: tag,
        y: tag.y - 30,
        alpha: 0,
        duration: 1200,
        onComplete: () => tag.destroy(),
      });
    }
    this.time.delayedCall(1500, () => {
      if (sprite.active) sprite.clearTint();
    });
  }

  private handleRescue() {
    if (this.rescued || this.gameOver) return;
    this.rescued = true;
    this.tryPlay("rescue", { volume: 1.0 });

    const cam = this.cameras.main;
    cam.flash(400, 255, 255, 255);

    this.ctx.socket.emit("game:event", {
      type: "anna:rescued",
      payload: {},
    });
    // alter Event-Typ für Backward-Compat
    this.ctx.socket.emit("game:event", {
      type: "rette-anna:rescued",
      payload: {},
    });

    this.showCenterText("Princess gerettet!", "#fff", "#ec4899");
  }

  private showCenterText(text: string, color: string, stroke: string) {
    const cam = this.cameras.main;
    const label = this.add
      .text(cam.scrollX + cam.width / 2, cam.scrollY + cam.height / 2, text, {
        fontFamily: "Inter, sans-serif",
        fontSize: "48px",
        color,
        stroke,
        strokeThickness: 6,
        align: "center",
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(1000);

    this.tweens.add({
      targets: label,
      scale: { from: 0.5, to: 1.2 },
      ease: "Back.Out",
      duration: 600,
    });
  }

  // ---------- Mitspieler ----------

  private ensureRemote(id: string, name: string, hexColor: string, character: string) {
    if (this.remotePlayers.has(id)) return;
    const color = Phaser.Display.Color.HexStringToColor(hexColor).color;
    const sprite = this.physics.add.sprite(100, GROUND_Y - 80, characterSpriteKey(character));
    sprite.setDepth(9);
    sprite.setAlpha(0.95);
    sprite.setData("tintColor", color);
    sprite.setImmovable(true);
    (sprite.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.remoteGroup.add(sprite);
    const label = this.add
      .text(100, GROUND_Y - 120, name, {
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        color: "#fff",
        backgroundColor: "rgba(0,0,0,0.4)",
        padding: { x: 4, y: 2 },
      })
      .setOrigin(0.5)
      .setDepth(9);
    this.remotePlayers.set(id, {
      sprite,
      label,
      color,
      targetX: sprite.x,
      targetY: sprite.y,
    });
  }

  private removeRemote(id: string) {
    const remote = this.remotePlayers.get(id);
    if (!remote) return;
    this.remoteGroup.remove(remote.sprite, true, true);
    remote.label.destroy();
    this.remotePlayers.delete(id);
    usePresenceStore.getState().remove(id);
  }

  // ---------- Socket ----------

  private bindSocketEvents() {
    const socket = this.ctx.socket;

    socket.on("room:update", this.handleRoomUpdate);
    socket.on("game:event", this.handleGameEvent);
  }

  private cleanupSocket() {
    const socket = this.ctx.socket;
    socket.off("room:update", this.handleRoomUpdate);
    socket.off("game:event", this.handleGameEvent);
    this.scale.off("resize", this.applyViewportZoom, this);
    this.audioUnsubscribe?.();
    this.audioUnsubscribe = null;
    this.musicSound?.stop();
    this.musicSound = null;
    usePresenceStore.getState().clear();
  }

  /**
   * Skaliert die Hauptkamera so, dass die komplette Welt-Höhe (`WORLD_HEIGHT`)
   * exakt in die verfügbare Display-Höhe passt. Im Hochkant-Modus auf
   * Touch-Geräten wird der untere Bereich für die Steuerung freigehalten,
   * sodass der Boden nicht hinter den Buttons verschwindet.
   */
  private applyViewportZoom = () => {
    const cam = this.cameras.main;
    if (!cam) return;
    const w = this.scale.width;
    const h = this.scale.height;
    const reserve = controlsReservedHeight(w, h);
    const viewportH = h - reserve;
    cam.setViewport(0, 0, w, viewportH);
    cam.setZoom(viewportH / WORLD_HEIGHT);
    cam.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  };

  private handleRoomUpdate = (room: {
    players: Array<{ id: string; name: string; color: string; character: string }>;
  }) => {
    const me = playerId(this);
    const seen = new Set<string>();
    for (const player of room.players) {
      if (player.id !== me) {
        this.ensureRemote(player.id, player.name, player.color, player.character);
        seen.add(player.id);
      }
    }
    for (const id of this.remotePlayers.keys()) {
      if (!seen.has(id)) this.removeRemote(id);
    }
  };

  private handleGameEvent = (event: { type: string; payload?: unknown }) => {
    const payload = event.payload as Record<string, unknown> | undefined;

    if (event.type === "rette-anna:state") {
      const from = payload?.from as string | undefined;
      if (!from) return;
      const remote = this.remotePlayers.get(from);
      if (!remote) return;
      const x = payload?.x as number;
      const y = payload?.y as number;
      // Statt für jedes Update einen neuen Tween anzulegen (stapeln sich
      // schnell und lassen Phaser unnötig schuften) merken wir uns nur die
      // Ziel-Position und interpolieren im update()-Loop smooth.
      remote.targetX = x;
      remote.targetY = y;
      if (typeof payload?.flip === "boolean") remote.sprite.setFlipX(payload.flip);
      usePresenceStore.getState().setPosition(from, x, y);
      return;
    }

    if (event.type === "rette-anna:shoot") {
      this.spawnRemoteBullet(payload);
      return;
    }

    if (event.type === "anna:monster-fire") {
      const monsterId = payload?.monsterId as string | undefined;
      if (monsterId) this.spawnFireballFromMonster(monsterId);
      return;
    }

    if (event.type === "anna:enemy-down") {
      const enemyId = payload?.enemyId as string | undefined;
      const byPlayerId = payload?.byPlayerId as string | undefined;
      const xp = payload?.xp as number | undefined;
      if (enemyId) this.destroyEnemyById(enemyId);
      // Im Training ignorieren wir XP-Updates, sonst Counter aktualisieren.
      const me = playerId(this);
      if (!this.trainingMode && byPlayerId === me && typeof xp === "number") {
        this.myXp = xp;
        this.emitHud();
      }
      return;
    }

    if (event.type === "anna:lives") {
      const lives = payload?.lives as number | undefined;
      const maxLives = (payload?.maxLives as number | undefined) ?? STARTING_LIVES;
      const deadPlayerId = payload?.deadPlayerId as string | undefined;
      const cause = (payload?.cause as string | undefined) ?? "unknown";
      if (typeof lives === "number") {
        this.scene.get("UIScene").events.emit("hud", {
          lives,
          maxLives,
          xp: this.myXp,
          training: this.trainingMode,
        });
      }
      // Anderen Spieler ebenfalls als getroffen anzeigen – wenn ich selbst der
      // tote Spieler bin, hat `handleHeroHit` das schon erledigt.
      if (deadPlayerId && deadPlayerId !== playerId(this)) {
        this.showRemotePlayerHit(deadPlayerId, cause);
      }
      return;
    }

    if (event.type === "anna:game-over") {
      const reason = payload?.reason as string | undefined;
      // Training: kein echtes Game-Over, nur kurze Bestätigung.
      if (reason === "trainingComplete") {
        this.showCenterText("Training abgeschlossen", "#fff", "#38bdf8");
        return;
      }
      this.triggerGameOver(reason === "rescued");
      return;
    }
  };

  private destroyEnemyById(enemyId: string) {
    const enemies = [
      ...this.monsters.getChildren(),
      ...this.flyingMonsters.getChildren(),
    ];
    for (const obj of enemies) {
      const m = obj as Phaser.Physics.Arcade.Sprite;
      if (m.getData("enemyId") === enemyId) {
        this.spawnHitEffect(m.x, m.y);
        m.destroy();
      }
    }
  }

  private emitHud() {
    this.scene.get("UIScene").events.emit("hud", {
      xp: this.myXp,
    });
  }

  private triggerGameOver(rescued: boolean) {
    if (this.gameOver) return;
    this.gameOver = true;
    // Den eigentlichen Endbildschirm zeichnet die UIScene als Overlay –
    // wir broadcasten hier nur das Event.
    this.scene.get("UIScene").events.emit("hud", {
      gameOver: true,
      rescued,
    });
  }

  private tryPlay(key: string, config?: Phaser.Types.Sound.SoundConfig) {
    if (!this.cache.audio.exists(key)) return;
    const sfx = effectiveSfxVolume(useAudioStore.getState());
    const baseVol = typeof config?.volume === "number" ? config.volume : 1;
    this.sound.play(key, { ...config, volume: baseVol * sfx });
  }
}

function playerId(scene: Phaser.Scene): string {
  const fromRegistry = scene.registry.get("playerId");
  return typeof fromRegistry === "string" ? fromRegistry : "";
}
